import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../console/api";
import { money, MonthlyBars } from "./FinanceDashboard";

export function FinanceCashflow({ filters, refreshKey }) {
  const [months, setMonths] = useState(12);
  const [data, setData] = useState(null);

  useEffect(() => {
    const q = new URLSearchParams({ months: String(months) });
    if (filters.kind) q.set("kind", filters.kind);
    if (filters.customer) q.set("customer", filters.customer);
    if (filters.payment_status) q.set("payment_status", filters.payment_status);
    api.get(`/admin/finance/cashflow?${q}`).then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load cashflow."));
  }, [months, filters.kind, filters.customer, filters.payment_status, refreshKey]);

  if (!data) return <div className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-[#6E1E3A]" /></div>;
  const t = data.totals;
  return (
    <div className="space-y-4" data-testid="finance-cashflow">
      <div className="flex items-center gap-2">
        <span className="text-[10px] tracking-[0.25em] uppercase font-semibold text-[#6E1E3A]">Show last</span>
        {[6, 12, 24].map((m) => (
          <button key={m} type="button" onClick={() => setMonths(m)} data-testid={`cashflow-months-${m}`}
            className={`h-8 px-3 text-xs font-semibold border ${months === m ? "bg-[#3E1F46] text-white border-[#3E1F46]" : "border-[#E8DFCB]"}`}>{m} months</button>
        ))}
        <span className="ml-auto text-xs text-[#7A6A55]">Net over period <strong className={t.net >= 0 ? "text-[#2F6B4F]" : "text-[#A33030]"}>{money(t.net)}</strong></span>
      </div>
      <MonthlyBars months={data.months} />
      <section className="bg-white border overflow-x-auto" style={{ borderColor: "#E8DFCB" }}>
        <table className="w-full text-sm">
          <thead><tr className="text-[9px] tracking-[0.25em] uppercase text-[#6E1E3A] text-left">
            <th className="px-4 py-2">Month</th><th className="px-2 py-2 text-right">Sales</th><th className="px-2 py-2 text-right">Cash in</th><th className="px-2 py-2 text-right">Other income</th>
            <th className="px-2 py-2 text-right">Refunds</th><th className="px-2 py-2 text-right">Expenses</th><th className="px-2 py-2 text-right">Net</th><th className="px-4 py-2 text-right">Running</th></tr></thead>
          <tbody>
            {data.months.map((m) => (
              <tr key={m.month} className="border-t" style={{ borderColor: "#F3EBDD" }} data-testid={`cashflow-row-${m.month}`}>
                <td className="px-4 py-2 font-semibold text-[#3C2A1E]">{m.month}</td>
                <td className="px-2 py-2 text-right text-[#3E1F46]">{money(m.sales)}</td>
                <td className="px-2 py-2 text-right text-[#2F6B4F]">{money(m.cash_in)}</td>
                <td className="px-2 py-2 text-right text-[#2F6B4F]">{m.manual_income ? money(m.manual_income) : ""}</td>
                <td className="px-2 py-2 text-right text-[#A33030]">{m.refunds ? money(m.refunds) : ""}</td>
                <td className="px-2 py-2 text-right text-[#A14C4C]">{m.expenses ? money(m.expenses) : ""}</td>
                <td className={`px-2 py-2 text-right font-semibold ${m.net >= 0 ? "text-[#2F6B4F]" : "text-[#A33030]"}`}>{money(m.net)}</td>
                <td className={`px-4 py-2 text-right font-semibold ${m.running >= 0 ? "text-[#2F6B4F]" : "text-[#A33030]"}`}>{money(m.running)}</td>
              </tr>
            ))}
            {data.months.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center italic text-[#9C8B73]">No movements in this period.</td></tr>}
          </tbody>
        </table>
      </section>
    </div>
  );
}
